import { useCallback } from 'react'
import { useSetAtom } from 'jotai'
import { useToggleTakingNotes } from '@renderer/hooks/audio/useAudioNoteState'
import { audioNoteStartsInFlightAtom, waitForAudioNoteSession } from '@renderer/hooks/audio/start-attempt'
import { useIsLoggedIn } from './useIsLoggedIn'
import { DetectedMeeting, GoogleMeetMeeting, RecallDetectedMeeting } from '@highlight/common'
import { useFeaturePrompt } from './useFeaturePrompt'
import { GlobalState } from '@highlight/ipc'
import type { AudioNoteState } from '@shared/types/AudioNoteState'

export interface StartAudioNoteOptions {
  source: 'assistant' | 'base' | 'toast' | 'tray'
  onSuccessfulStart?: () => void
  meeting?: DetectedMeeting | GoogleMeetMeeting | RecallDetectedMeeting
}

/**
 * Audio notes should always be started using this hook.
 * It handles all the logic for starting audio notes, including:
 * - Checking if the user is logged in
 * - Showing the tutorial if needed
 * - Showing the cloud transcription setting if needed
 *
 * Capture permissions (microphone + system audio) are NOT requested here. The
 * main process preflights them in AudioNoteStateManager.startRecording: if one
 * is missing it routes the user to Settings → Meetings (the unified permissions
 * banner) and waits for the grant, then starts — so the OS prompt never fires
 * straight from a click here.
 */
export const useStartAudioNote = () => {
  const toggleTakingNotes = useToggleTakingNotes()
  const isLoggedIn = useIsLoggedIn()
  const { setPromptStatus: setMeetingNotesPromptStatus } = useFeaturePrompt('meeting-notes')
  const setStartsInFlight = useSetAtom(audioNoteStartsInFlightAtom)

  const startAudioNote = useCallback(
    async (options: StartAudioNoteOptions) => {
      const { onSuccessfulStart, meeting } = options

      if (!isLoggedIn) {
        return
      }

      setMeetingNotesPromptStatus('should-not-show')
      // Only ever start here — never toggle off. A meeting parked on a missing
      // permission may have already begun recording in main once granted;
      // toggleTakingNotes treats a non-idle status as "stop" and would cancel
      // that fresh session.
      const status = GlobalState.get<AudioNoteState>('audio-note-state')?.status
      if (status !== undefined && status !== 'idle' && status !== 'saved') {
        onSuccessfulStart?.()
        return
      }
      // `onSuccessfulStart` fires before the start settles — it opens the
      // Meeting View, which waits on the session — so the in-flight count is
      // what tells that view whether a session is still coming. While main
      // says one is (begun here, or already under way from a start this
      // renderer didn't make), stay counted until it shows in this
      // renderer's state.
      setStartsInFlight((count) => count + 1)
      const started = toggleTakingNotes(meeting)
      onSuccessfulStart?.()
      try {
        if (await started) await waitForAudioNoteSession()
      } catch (error) {
        // Main logs and toasts the failure itself, and every caller fires this
        // without awaiting, so the catch only stops an unhandled rejection.
        console.error('[useStartAudioNote] start failed:', error)
      } finally {
        setStartsInFlight((count) => count - 1)
      }
    },
    [toggleTakingNotes, isLoggedIn, setMeetingNotesPromptStatus, setStartsInFlight],
  )

  return { startAudioNote }
}
